"use client"

import { cn } from "@/lib/utils"
import { useSidebarStore } from "@/lib/stores/sidebar-store"
import { X, PanelRightClose } from "lucide-react"
import { Button } from "@/components/ui/button"
import { StrategicFeed } from "@/components/intelligence/strategic-feed"
import { ThinkingVisual } from "@/components/ui/thinking-visual"
import { useEffect, useState } from "react"

interface ToolDrawerProps {
  conversationId?: string
  pendingCount?: number
}

export function ToolDrawer({ conversationId, pendingCount = 0 }: ToolDrawerProps) {
  const { toolDrawerOpen, toolDrawerContent, closeToolDrawer } = useSidebarStore()
  const [userId, setUserId] = useState<string>("")
  const [tenantId, setTenantId] = useState<string>("")

  useEffect(() => {
    async function fetchUser() {
      try {
        const response = await fetch("/api/user")
        if (response.ok) {
          const data = await response.json()
          setUserId(data.id || "")
          setTenantId(data.tenant_id || "")
        }
      } catch (error) {
        console.error("[v0] ToolDrawer: Failed to fetch user:", error)
      }
    }
    fetchUser()
  }, [])

  return (
    <>
      {/* Mobile overlay backdrop */}
      {toolDrawerOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={closeToolDrawer} aria-hidden="true" />
      )}

      {/* Right panel - full-height sibling column on desktop */}
      <aside
        className={cn(
          // Layout positioning - mobile overlay, desktop static column
          "fixed lg:static",
          "inset-y-0 right-0 z-50 lg:z-0",
          "flex flex-col",
          // Desktop: always rendered, fixed width when open
          "lg:flex",
          toolDrawerOpen ? "w-80 lg:w-[340px]" : "lg:w-0 lg:opacity-0 lg:pointer-events-none",
          "max-w-[85vw] lg:max-w-none",
          "min-h-full",
          // Mobile transform animation
          "transform transition-all duration-300 ease-out lg:duration-200",
          toolDrawerOpen ? "translate-x-0" : "translate-x-full lg:translate-x-0",
          // Mobile: full black background, Desktop: contextual surface (darker than center)
          "bg-background lg:bg-[var(--surface-context)]",
          toolDrawerOpen && "lg:shadow-[inset_4px_0_8px_rgba(0,0,0,0.18)]",
        )}
        role="complementary"
        aria-label="Advisor Focus panel"
        aria-hidden={!toolDrawerOpen}
      >
        {/* Desktop header - matches center column height (h-16) */}
        {toolDrawerOpen && (
          <div className="hidden lg:flex h-16 items-center justify-between px-4 pb-1 shrink-0">
            {/* Replace "Advisor Focus" with "Advisor" + ThinkingVisual icon */}
            <div className="flex items-center gap-3">
              <div className="scale-[0.70]">
                <ThinkingVisual
                  config={{
                    active: true,
                    breathMode: "calm", // Advisor presence indicator, calm by default
                    visualEmphasis: "normal",
                    motionComplexity: "still", // Breathing only, no drift
                    version: "1.1",
                  }}
                  context="inline"
                />
              </div>
              {/* </CHANGE> */}
              <span className="text-[22px] font-display font-normal text-foreground">Advisor</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-lg"
              onClick={closeToolDrawer}
              aria-label="Close advisor panel"
            >
              {/* Mute close icon to match left nav tone */}
              <PanelRightClose className="h-4 w-4 text-[var(--text-secondary)]" />
            </Button>
          </div>
        )}

        {/* Mobile header */}
        {toolDrawerOpen && (
          <div className="flex lg:hidden h-12 items-center justify-between px-4 shrink-0 border-b border-[var(--border)]">
            {/* Update mobile header to "Advisor" (no icon on mobile) */}
            <span className="text-base font-display font-normal text-foreground">Advisor</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-lg"
              onClick={closeToolDrawer}
              aria-label="Close advisor panel"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        {toolDrawerOpen && (
          <div className="flex-1 overflow-hidden min-h-0">
            {toolDrawerContent === "feed" && (
              <StrategicFeed userId={userId} tenantId={tenantId} conversationId={conversationId} />
            )}
            {toolDrawerContent === "brain" && (
              <div className="flex items-center justify-center h-full p-4 text-center">
                <p className="text-sm text-[var(--text-secondary)]">Memory panel coming soon</p>
              </div>
            )}
            {!toolDrawerContent && (
              <div className="flex items-center justify-center h-full p-4 text-center">
                <p className="text-sm text-[var(--text-secondary)]">No tool selected</p>
              </div>
            )}
          </div>
        )}
      </aside>
    </>
  )
}
